import { BotContact, BotContactFilters } from '@interfaces/reports/botContacts';
import {
  CartAbandonmentReport,
  CartAbandonmentReportFilters,
} from '@interfaces/reports/cartAbandonments';
import { ReportResponse } from '@interfaces/reports';
import { FindReportDto } from '@interfaces/reports';
import { fetcher } from '@utils/fetcher';
import { NewUsers } from '@interfaces/reports/newUsers';
import { Sale, SalesFilters } from '@interfaces/reports/sales';
import {
  GetPayoutDetailsResponse,
  ReceiptHistory,
  ReceiptHistoryFilters,
} from '@interfaces/reports/receiptHistory';
import {
  ReceiptForecast,
  ReceiptForecastsFilters,
} from '@interfaces/reports/receiptForecasts';
import { Refund, RefundsFilters } from '@interfaces/reports/refunds';
import {
  Subscription,
  SubscriptionFilters,
} from '@interfaces/reports/subscriptions';
import {
  SalesSummary,
  SalesSummaryFilters,
} from '@interfaces/reports/salesSummary';
import {
  SalesHistory,
  SalesHistoryFilters,
} from '@interfaces/reports/salesHistory';
import { ReferrerCommission } from '@interfaces/reports/referrerCommissions';
import {
  AffiliatedSale,
  AffiliatedSalesFilters,
} from '@interfaces/reports/affiliatedSales';
import {
  SaleAsAffiliate,
  SalesAsAffiliateFilters,
} from '@interfaces/reports/salesAsAffiliate';
import { DashboardFilters } from '@interfaces/dashboard';
import {
  SaleByUser,
  SalesByUserFilters,
} from '@interfaces/reports/salesByUser';
import {
  OfferPerformanceFilters,
  OfferPerformanceResponse,
} from '@interfaces/reports/offerPerformance';
import {
  PaymentConversionFilters,
  PaymentConversionResponse,
} from '@interfaces/reports/paymentConversion';
import {
  BotFunnelFilters,
  BotFunnelResponse,
} from '@interfaces/reports/botFunnel';
import {
  RecurringRevenueFilters,
  RecurringRevenueResponse,
} from '@interfaces/reports/recurringRevenue';

import {
  CustomerValueFilters,
  CustomerValueResponse,
} from '@interfaces/reports/customerValue';
import {
  TimeToConvertFilters,
  TimeToConvertResponse,
} from '@interfaces/reports/timeToConvert';
import {
  RemarketingAttributionFilters,
  RemarketingAttributionResponse,
} from '@interfaces/reports/remarketingAttribution';

export enum ApiReportUrls {
  BotContacts = '/api/reports/botContacts',
  BotContactsCustom = '/api/reports/botContacts/custom', // TO REMOVE
  CartAbandonments = '/api/reports/cartAbandonments',
  NewUsers = '/api/reports/newUsers',
  ReceiptForecasts = '/api/reports/receiptForecasts',
  ReceiptHistory = '/api/reports/receiptHistory',
  Refunds = '/api/reports/refunds',
  Sales = '/api/reports/sales',
  SalesHistory = '/api/reports/salesHistory',
  SalesSummary = '/api/reports/salesSummary',
  Subscriptions = '/api/reports/subscriptions',
  ReferrerCommissions = '/api/reports/referrerCommissions',
  AffiliatedSales = '/api/reports/affiliatedSales',
  SalesAsAffiliate = '/api/reports/salesAsAffiliate',
  SalesByUser = '/api/reports/salesByUser',
  OfferPerformance = '/api/reports/offerPerformance',
  PaymentConversion = '/api/reports/paymentConversion',
  BotFunnel = '/api/reports/botFunnel',
  RecurringRevenue = '/api/reports/recurringRevenue',
  CustomerValue = '/api/reports/customerValue',
  TimeToConvert = '/api/reports/timeToConvert',
  RemarketingAttribution = '/api/reports/remarketingAttribution',
}

export const findBotContacts = async (
  dto: FindReportDto<BotContactFilters>,
): Promise<ReportResponse<BotContact[]>> =>
  fetcher(ApiReportUrls.BotContacts, {
    method: 'POST',
    body: dto,
  });

// TO REMOVE
export const findBotContactsCustom = async (
  dto: FindReportDto<BotContactFilters>,
): Promise<ReportResponse<BotContact[]>> =>
  fetcher(ApiReportUrls.BotContactsCustom, {
    method: 'POST',
    body: dto,
  });
// TO REMOVE

export const findCartAbandonments = async (
  dto: FindReportDto<CartAbandonmentReportFilters>,
): Promise<ReportResponse<CartAbandonmentReport[]>> =>
  fetcher(ApiReportUrls.CartAbandonments, {
    method: 'POST',
    body: dto,
  });

export const findSales = async (
  dto: FindReportDto<SalesFilters>,
): Promise<ReportResponse<Sale[]>> =>
  fetcher(ApiReportUrls.Sales, {
    method: 'POST',
    body: dto,
  });

export const findReceiptHistory = async (
  dto: FindReportDto<ReceiptHistoryFilters>,
): Promise<ReportResponse<ReceiptHistory[]>> =>
  fetcher(ApiReportUrls.ReceiptHistory, {
    method: 'POST',
    body: dto,
  });

export const getPayoutDetails = async (
  id: number,
  dto: FindReportDto,
): Promise<ReportResponse<GetPayoutDetailsResponse>> =>
  fetcher(`${ApiReportUrls.ReceiptHistory}/${id}/details`, {
    method: 'POST',
    body: dto,
  });

export const findReceiptForecasts = async (
  dto: FindReportDto<ReceiptForecastsFilters>,
): Promise<ReportResponse<ReceiptForecast[]>> =>
  fetcher(ApiReportUrls.ReceiptForecasts, {
    method: 'POST',
    body: dto,
  });

export const findRefunds = async (
  dto: FindReportDto<RefundsFilters>,
): Promise<ReportResponse<Refund[]>> =>
  fetcher(ApiReportUrls.Refunds, {
    method: 'POST',
    body: dto,
  });

export const findSubscriptions = async (
  dto: FindReportDto<SubscriptionFilters>,
): Promise<ReportResponse<Subscription[]>> =>
  fetcher(ApiReportUrls.Subscriptions, {
    method: 'POST',
    body: dto,
  });

export const findNewUsers = async (
  filters: Partial<DashboardFilters>,
): Promise<ReportResponse<NewUsers>> =>
  fetcher(ApiReportUrls.NewUsers, {
    method: 'POST',
    body: filters,
  });

export const findSalesSummary = async (
  filters: SalesSummaryFilters,
): Promise<ReportResponse<SalesSummary>> =>
  fetcher(ApiReportUrls.SalesSummary, {
    method: 'POST',
    body: filters,
  });

export const findSalesHistory = async (
  filters: SalesHistoryFilters,
): Promise<ReportResponse<SalesHistory[]>> =>
  fetcher(ApiReportUrls.SalesHistory, {
    method: 'POST',
    body: filters,
  });

export const findReferrerCommissions = async (
  filters: FindReportDto,
): Promise<ReportResponse<ReferrerCommission[]>> =>
  fetcher(ApiReportUrls.ReferrerCommissions, {
    method: 'POST',
    body: filters,
  });

export const findAffiliatedSales = async (
  filters: FindReportDto<AffiliatedSalesFilters>,
): Promise<ReportResponse<AffiliatedSale[]>> =>
  fetcher(ApiReportUrls.AffiliatedSales, {
    method: 'POST',
    body: filters,
  });

export const findSalesAsAffiliate = async (
  filters: FindReportDto<SalesAsAffiliateFilters>,
): Promise<ReportResponse<SaleAsAffiliate[]>> =>
  fetcher(ApiReportUrls.SalesAsAffiliate, {
    method: 'POST',
    body: filters,
  });

export const findSalesByUser = async (
  dto: FindReportDto<SalesByUserFilters>,
): Promise<ReportResponse<SaleByUser[]>> =>
  fetcher(ApiReportUrls.SalesByUser, {
    method: 'POST',
    body: dto,
  });

export const findOfferPerformanceReport = async (
  dto: FindReportDto<OfferPerformanceFilters>,
): Promise<ReportResponse<OfferPerformanceResponse>> =>
  fetcher(ApiReportUrls.OfferPerformance, {
    method: 'POST',
    body: dto,
  });

export const findPaymentConversion = async (
  filters: PaymentConversionFilters,
): Promise<ReportResponse<PaymentConversionResponse>> =>
  fetcher(ApiReportUrls.PaymentConversion, {
    method: 'POST',
    body: filters,
  });

export const findBotFunnel = async (
  filters: BotFunnelFilters,
): Promise<ReportResponse<BotFunnelResponse>> =>
  fetcher(ApiReportUrls.BotFunnel, {
    method: 'POST',
    body: filters,
  });

export const findRecurringRevenue = async (
  filters: RecurringRevenueFilters,
): Promise<ReportResponse<RecurringRevenueResponse>> =>
  fetcher(ApiReportUrls.RecurringRevenue, {
    method: 'POST',
    body: filters,
  });

export const findCustomerValue = async (
  filters: CustomerValueFilters,
): Promise<ReportResponse<CustomerValueResponse>> =>
  fetcher(ApiReportUrls.CustomerValue, {
    method: 'POST',
    body: filters,
  });

export const findTimeToConvert = async (
  filters: TimeToConvertFilters,
): Promise<ReportResponse<TimeToConvertResponse>> =>
  fetcher(ApiReportUrls.TimeToConvert, {
    method: 'POST',
    body: filters,
  });

export const findRemarketingAttribution = async (
  filters: RemarketingAttributionFilters,
): Promise<ReportResponse<RemarketingAttributionResponse>> =>
  fetcher(ApiReportUrls.RemarketingAttribution, {
    method: 'POST',
    body: filters,
  });

export const exportVibReport = async (
  reportUrl: string,
  dto: FindReportDto,
): Promise<ReportResponse<any>> =>
  fetcher(reportUrl, {
    method: 'POST',
    body: dto,
  });
